/* eslint-disable operator-linebreak */
import React, { useState, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { TableColumn } from 'react-data-table-component';

import Arrow from 'src/assets/arrow-left-black.svg';

import { SearchInput } from 'pg-ui-library';

import { SettingsButton } from 'src/components';

import { IBenefitsVersionManagement } from 'src/interfaces';
import useDebounceSearchTerm from 'src/hooks/useDebounceSearchTerm';
import {
  Container,
  ItemContainer,
  ArrowBack,
  NavTextLink,
  ItemContainerHeader,
  PageTitle,
  HeaderLeftItems,
  HeaderRightItems,
  HeaderLeftFirstItem,
  HeaderLeftSecondItem,
  TableSettingContainer,
} from './styles';

import { BenefitsVersionTable, benefitsVersionTableColumns } from '../benefits-version-table';
import { BenefitVersionModal } from '../benefit-version-modal';

interface IBenefitVersionContext {
  benefitVersion: IBenefitsVersionManagement | null;
  benefitVersionEditModalIsOpen: boolean;
  // eslint-disable-next-line no-unused-vars
  setSelectedBenefitVersion: (b: IBenefitsVersionManagement) => void;
  // eslint-disable-next-line no-unused-vars
  setBenefitVersionEditModalIsOpen: (b: boolean) => void;
  clearSelectedBenefitVersion: () => void;
}

const useStore = (): IBenefitVersionContext => {
  const [benefitVersion, setBenefitVersion] = useState<IBenefitsVersionManagement | null>(null);
  const [benefitVersionEditModalIsOpen, setBenefitVersionEditModal] = useState<boolean>(false);

  const setSelectedBenefitVersion = (b: IBenefitsVersionManagement): void => {
    setBenefitVersion(b);
  };

  const clearSelectedBenefitVersion = (): void => {
    setBenefitVersion(null);
  };

  const setBenefitVersionEditModalIsOpen = (value: boolean): void => {
    setBenefitVersionEditModal(value);
  };

  return {
    benefitVersion,
    benefitVersionEditModalIsOpen,
    setSelectedBenefitVersion,
    clearSelectedBenefitVersion,
    setBenefitVersionEditModalIsOpen,
  };
};

const BenefitVersionContext = createContext<IBenefitVersionContext | null>(null);

export const BenefitVersionContextProvider: React.FC = ({ children }) => (
  <BenefitVersionContext.Provider value={useStore()}>{children}</BenefitVersionContext.Provider>
);

export const useBenefitVersion = (): IBenefitsVersionManagement | null =>
  useContext(BenefitVersionContext)?.benefitVersion ?? null;

// eslint-disable-next-line no-unused-vars
export const useSetBenefitVersion = (): ((b: IBenefitsVersionManagement) => void) =>
  useContext(BenefitVersionContext)?.setSelectedBenefitVersion ?? (() => {});

export const useBenefitVersionEditModalIsOpen = (): boolean =>
  useContext(BenefitVersionContext)?.benefitVersionEditModalIsOpen ?? false;

// eslint-disable-next-line no-unused-vars
export const useSetBenefitVersionEditModal = (): ((value: boolean) => void) =>
  useContext(BenefitVersionContext)?.setBenefitVersionEditModalIsOpen ?? (() => {});

export const useClearBenefitVersion = (): (() => void) =>
  useContext(BenefitVersionContext)?.clearSelectedBenefitVersion ?? (() => {});

export const BenefitVersionContextWrapper: React.FC<{
  searchTerm: string;
  columns: TableColumn<IBenefitsVersionManagement>[];
}> = ({ searchTerm, columns }) => (
  <BenefitVersionContextProvider>
    <BenefitVersionModal />
    <BenefitsVersionTable searchTerm={searchTerm} columns={columns} />
  </BenefitVersionContextProvider>
);

export const BenefitsVersionManagement: React.FC = () => {
  const { t } = useTranslation();
  const [columns, setColumns] = useState<TableColumn<IBenefitsVersionManagement>[]>(benefitsVersionTableColumns);

  const { searchTerm, setSearchTerm, debouncedValue } = useDebounceSearchTerm(500);

  return (
    <Container>
      <ItemContainerHeader>
        <HeaderLeftItems>
          <HeaderLeftFirstItem>
            <NavTextLink to="/benefits">
              <ArrowBack src={Arrow} />
            </NavTextLink>
            <PageTitle>{t('benefits.versionManagement.title')}</PageTitle>
          </HeaderLeftFirstItem>
          <HeaderLeftSecondItem />
        </HeaderLeftItems>
        <HeaderRightItems>
          <SearchInput
            value={searchTerm}
            placeholder="Search..."
            handleChange={event => {
              setSearchTerm(event.target.value);
            }}
          />
        </HeaderRightItems>
      </ItemContainerHeader>
      <ItemContainer>
        <TableSettingContainer>
          <SettingsButton columns={columns} setColumns={setColumns} />
        </TableSettingContainer>
        <BenefitVersionContextWrapper searchTerm={debouncedValue} columns={columns} />
      </ItemContainer>
    </Container>
  );
};
